// Typed-code suggestions: a manager typing a bin, product, order or shipment code by hand is offered
// what it may mean, so the whole code need not be typed. A barcode reader's burst never opens the
// list (it ends with Enter long before the field stands still, and its pace is not a person's), and
// the warehouse staff, who must read codes with a reader, never get one.
import {
  INBOUND_STATUS_LABELS,
  LOCATION_TYPE_LABELS,
  ORDER_STATUS_LABELS,
  type CodeSuggestion,
  type CodeSuggestionKind,
  type OrderStatus,
} from '@wms/domain';
import { judgeScan, type ScanTrace } from './scan';

/** Fewest characters asked about */
export const SUGGEST_MIN_CHARS = 2;
/** The field stands still this long before the list is asked for (a reader is done well before) */
export const SUGGEST_IDLE_MS = 250;

/** What a field offers while a manager types into it. */
export interface SuggestSource {
  readonly kinds: readonly CodeSuggestionKind[];
  /** products of one seller only (mal kabul: the shipment's seller) */
  readonly tenantId?: string | null;
  /** orders in these statuses only (the packing desk: picked, packing) */
  readonly orderStatuses?: readonly OrderStatus[];
  readonly limit?: number;
}

/** The question for /api/wms/suggest. */
export function suggestPath(warehouseId: string, q: string, source: SuggestSource): string {
  const query = new URLSearchParams({ warehouseId, q: q.trim(), kinds: source.kinds.join(',') });
  if (source.tenantId) query.set('tenantId', source.tenantId);
  if (source.orderStatuses && source.orderStatuses.length > 0) query.set('orderStatus', source.orderStatuses.join(','));
  if (source.limit) query.set('limit', String(source.limit));
  return `/api/wms/suggest?${query}`;
}

/**
 * Whether the field's text is a person's typing worth asking about: at least SUGGEST_MIN_CHARS
 * characters that did not come at a reader's pace (judgeScan; two characters: a person leaves a gap
 * between them, a reader none). Pasted text is a person's too; text the field never saw go in (the
 * screen put it there: a link's search, a picked suggestion) is nobody's typing.
 */
export function typedByPerson(trace: ScanTrace, value: string): boolean {
  if (value.trim().length < SUGGEST_MIN_CHARS) return false;
  const verdict = judgeScan(trace, value);
  if (verdict.reader || verdict.reason === 'none') return false;
  if (verdict.reason !== 'short') return true;
  return verdict.shape.gaps > 0 && verdict.shape.fast < verdict.shape.gaps;
}

const percent = (ratio: number) => `%${Math.round(ratio * 100)}`;

/** A suggestion in two lines: the code to pick, then what it is (area and fill and sellers, seller and name, …). */
export function suggestionText(s: CodeSuggestion): { readonly main: string; readonly detail: string } {
  const join = (parts: readonly (string | null | false)[]) => parts.filter((p): p is string => Boolean(p)).join(' · ');
  switch (s.kind) {
    case 'bin':
      return {
        main: s.code,
        detail: join([
          LOCATION_TYPE_LABELS[s.type],
          s.units === 0 ? 'boş' : `${s.units} adet${s.fill !== null ? `, ${percent(s.fill)} dolu` : ''}`,
          s.sellers.length > 0 && s.sellers.join(', '),
          s.isBlocked && 'sayımda (kilitli)',
        ]),
      };
    case 'product':
      return { main: s.sku, detail: join([s.seller, s.name, s.barcode]) };
    case 'order':
      return { main: s.code, detail: join([s.seller, ORDER_STATUS_LABELS[s.status]]) };
    case 'inbound':
      return { main: s.code, detail: join([s.seller, s.reference, INBOUND_STATUS_LABELS[s.status]]) };
  }
}

/** Short words for the kind of a suggestion, shown when a list mixes kinds. */
export const SUGGESTION_KIND_LABELS: Readonly<Record<CodeSuggestionKind, string>> = { bin: 'Göz', product: 'Ürün', order: 'Sipariş', inbound: 'Gönderi' };

/**
 * The highlighted row after an arrow key: down from the typed text (-1) to the first row and on,
 * past the last back to the typed text; up the other way. Any other key leaves it.
 */
export function moveHighlight(index: number, key: string, count: number): number {
  if (count === 0) return -1;
  if (key === 'ArrowDown') return index >= count - 1 ? -1 : index + 1;
  if (key === 'ArrowUp') return index <= -1 ? count - 1 : index - 1;
  return index;
}
